/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { communicationAttachmentFromFile } from '@gh/builder-shared';
import {
  CommunicationDataDto,
  CommunicationListItemDto,
  ContactListItemDto,
  ContracturalCompletionRequestDto,
  ContracturalCompletionResponseDto,
  DocInfoDto,
  EDocDto,
  InspectionItemDto,
  InvoiceType,
  isLendersScheduleCode,
  JobBasicInfoDto,
  JobChangeOrderDto,
  JobChangeOrderTabListDto,
  JobChangeOrderTabListTotalDto,
  JobChecklistDto,
  JobDatesTabInfoDto,
  JobDrawsDto,
  JobDrawsInvoiceDto,
  JobDrawsLendersScheduleItemDto,
  JobDrawsPaymentDto,
  JobDrawsPaymentScheduleItemDto,
  JobDrawsRecalculateRequestDto,
  JobDrawsRuleDto,
  JobEDocLogDto,
  JobEDocOwnerSetDto,
  JobEDocSigningDocumentDto,
  JobEDocSigningRequestDto,
  JobGeneralTabInfoDto,
  JobInsuranceDto,
  JobRfqDto,
  JobSharedDocListItemDto,
  JobSharedDocNewSetDto,
  JobSharedDocType,
  JobStageListItemDto,
  JobWarrantyFilterDto,
  JobWarrantyTaskDto,
  LetterDto,
  UserAssignedRoleDto,
  UserListItemDto,
} from '@gh/builder-shared-data';
import { FileListItem } from '@gh/common-internal-shared';
import {
  createArrayHelper,
  EMPTY_FORM_STATE_SNAPSHOT,
  EntityConfig,
  entityValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  IS_REGION_AU_OR_NZ,
  NumberTypes,
} from '@gh/common-shared';
import { ID, WithRecalculationNeeded, WithVersion } from '@gh/common-shared-data';
import { currentDate } from '@gh/core-util';
import {
  compact,
  find,
  first,
  get,
  isEmpty,
  isNil,
  map,
  omit,
  omitBy,
  pick,
  sortBy,
  stubTrue,
  sumBy,
} from 'lodash';

export type JobWarrantyTask = JobWarrantyTaskDto;
export type JobBasicInfo = JobBasicInfoDto;
export type JobGeneralTabInfo = JobGeneralTabInfoDto;
export type JobStageListItem = JobStageListItemDto;
export type JobDatesTabInfo = JobDatesTabInfoDto;
export type JobDatesSave = { basicInfo?: JobBasicInfo; datesInfo: JobDatesTabInfo };
export type ContracturalCompletionRequest = ContracturalCompletionRequestDto;
export type ContracturalCompletionResponse = ContracturalCompletionResponseDto;

export type JobWarrantyFilter = JobWarrantyFilterDto;
export const jobWarrantyTaskFromDto = (task: JobWarrantyTask) => ({
  ...task,
  supplierName: task.supplier ? task.supplier.name : task.supplierName,
});

export const newEmptyWarrantyTask = (jobId: ID) => ({
  id: void 0,
  jobId,
  enteredDate: currentDate(),
  description: '',
  toBeCompletedDate: void 0,
  supplierName: '',
});

export const jobGeneralInfoToDto = (info: JobGeneralTabInfo) => <JobGeneralTabInfo>({
  ...info,
  estimator: info.estimator ? omit(info.estimator, 'fullName') : void 0,
  supervisor: info.supervisor ? omit(info.supervisor, 'fullName') : void 0,
  jobAdmin: info.jobAdmin ? omit(info.jobAdmin, 'fullName') : void 0,
  financeCompany: info.financeCompany ? omit(info.financeCompany, 'name') : void 0,
  county: info.county ? omit(info.county, 'name') : void 0,
});

export const getJobCommunicationListItemId = (communicationListItem: CommunicationListItem) =>
  communicationListItem.communicationId;

export type ContactListItem = ContactListItemDto;
export type UserAssignedRole = UserAssignedRoleDto;
export type UserListItem = UserListItemDto;
export type JobRfq = JobRfqDto;
export type JobRfqInspectionItem = InspectionItemDto;
export const rfqFromDto = (dto: JobRfqDto) => <JobRfqDto>({
  ...dto,
  isInspected: !isNil(dto.inspectedDate),
  inspections: isNil(dto.inspections) ? [] : dto.inspections,
});
export type DocInfo = DocInfoDto;
export type Letter = LetterDto;
export type LetterListItem = {
  fileName: string;
};

export type JobChecklist = JobChecklistDto;
export type JobChecklistItem = {
  id: ID;
  name: string;
  isCompleted: boolean;
};

export type JobEDocOwnerSet = JobEDocOwnerSetDto;
export type JobEDocLog = JobEDocLogDto;
export type JobEDocLogListItem = EDocDto;
export type JobEDocDetails = {
  ownerSet: JobEDocOwnerSet;
  log: JobEDocLog;
  logLoading: boolean;
};

export type JobEDocDocumentSet = {
  contract: FileListItem[];
  specification: FileListItem[];
  plans: FileListItem[];
  authorityToCommence: FileListItem[];
  colorSelection: FileListItem[];
  otherDocuments: FileListItem[];
};

export type JobEDocSigningRequest = JobEDocSigningRequestDto;
export type JobEDocSigningDocument = JobEDocSigningDocumentDto;

export type JobSharedDocSet = {
  contract: FileListItem[];
  colorSelection: FileListItem[];
  specification: FileListItem[];
  coQuotations: FileListItem[];
  plans: FileListItem[];
  jobLetters: FileListItem[];
};

export type JobSharedDocListItem = JobSharedDocListItemDto;

export type JobDrawsRecalculateRequest = JobDrawsRecalculateRequestDto;
export type JobDrawsDetailsRule = JobDrawsRuleDto;
export type JobDrawsDetailsPayment = JobDrawsPaymentDto;
export type JobDrawsPaymentScheduleItem = JobDrawsPaymentScheduleItemDto;

export enum JobDrawsLenderScheduleItemPlacement {
  Regular = 'Regular',
  Total = 'Total',
}

export type JobDrawsLendersScheduleItem = JobDrawsLendersScheduleItemDto & {
  placement: JobDrawsLenderScheduleItemPlacement;
};

export enum JobDrawsInvoicePlacement {
  Regular = 'Regular',
  TotalContract = 'TotalContract',
  TotalChangeOrders = 'TotalChangeOrders',
  TotalDraws = 'TotalDraws',
  TotalCrNotes = 'TotalCrNotes',
}

export type JobDrawsDetailsInvoice = JobDrawsInvoiceDto & {
  placement: JobDrawsInvoicePlacement;
};
export type JobDrawsDetailsInfo = {
  contractAmount: number;
  contractAmountWithGst: number;
  contractAmountLenders: number;
  contractDrawnAmount: number;
  contractToBe: number;

  variationsAmount: number;
  variationsAmountWithGst: number;
  variationsDrawnAmount: number;
  variationsToBe: number;

  totalDrawnAmount: number;
  totalAmountWithGst: number;
  totalAmount: number;
  totalToBe: number;

  isContractAmountManual: boolean;
  isContractAmountWithGstManual: boolean;
};

export enum JobDrawsDetailsTab { History = 'HISTORY', Schedule = 'SCHEDULE' }

export type JobDrawsDetails = WithRecalculationNeeded<WithVersion<{
  originalRuleId?: ID;
  currentRuleId?: ID;
  selectedInvoiceId?: ID;
  selectedTab: JobDrawsDetailsTab;
  rules: JobDrawsDetailsRule[];
  info: FormStateSnapshot<JobDrawsDetailsInfo>;
  invoices: JobDrawsDetailsInvoice[];
  paymentSchedule: JobDrawsPaymentScheduleItem[];
  lendersSchedule: JobDrawsLendersScheduleItem[];
  isLendersScheduleRefreshed: boolean;
  isDrawRuleModified: boolean;
  isNotProceeding: boolean;
  isFinalized: boolean;
}>>;

export const JOB_DRAWS_DETAILS_INIT: JobDrawsDetails = {
  selectedTab: JobDrawsDetailsTab.History,
  info: EMPTY_FORM_STATE_SNAPSHOT,
  rules: [],
  invoices: [],
  paymentSchedule: [],
  lendersSchedule: [],
  isLendersScheduleRefreshed: false,
  isDrawRuleModified: false,
  isNotProceeding: false,
  isFinalized: false,
};

export function letterListFromDto(dto: string[]): LetterListItem[] {
  return dto.map((fileName: string) => ({ fileName }));
}

export type JobVariationResponse = JobChangeOrderTabListDto;
export type JobVariation = JobChangeOrderDto;
export type JobVariationTotal = JobChangeOrderTabListTotalDto;
export type JobInsurance = JobInsuranceDto;
export type CommunicationListItem = CommunicationListItemDto;
export type CommunicationData = CommunicationDataDto;
export const getCommunicationId = (communication: CommunicationListItem) => communication.communicationId;
export type CommunicationListFilters = {
  showSms: boolean;
  showEmail: boolean;
  showDoc: boolean;
};

export const getDisplayedJobInsurance = (insurance: JobInsurance, original: JobInsurance): JobInsurance => {
  const isPolicyNumberEmpty = isEmpty(insurance.insurancePolicyNumber);

  return {
    ...insurance,
    insuranceCompanyName: isPolicyNumberEmpty ? void 0 : original.insuranceCompanyName,
    insuranceAmount: isPolicyNumberEmpty ? void 0 : original.insuranceAmount,
    insuranceStartDate: isPolicyNumberEmpty ? void 0 : original.insuranceStartDate,
  };
};

export const jobCommunicationListArr = createArrayHelper(getJobCommunicationListItemId);

export const getJobWarrantyTaskId = (task: JobWarrantyTask) => task.id;
export const getJobWarrantyTaskEnteredDate = (task: JobWarrantyTask) => task.enteredDate;

export const jobDrawsDetailsFromDto = (dto: JobDrawsDto): JobDrawsDetails => {
  const invoices = sortBy(dto.invoices, 'invoiceNumber').map((invoice) => ({
    ...invoice,
    payments: sortBy(invoice.payments, 'reference'),
    placement: JobDrawsInvoicePlacement.Regular,
  }));

  const lenderScheduleOrderCostTotal = sumBy(dto.lendersSchedule, 'orderCost');
  const currentRule = find(dto.drawRules, { id: dto.drawRuleId });

  return {
    originalRuleId: dto.drawRuleId,
    currentRuleId: dto.drawRuleId,
    selectedTab: JobDrawsDetailsTab.History,
    info: formStateSnapshot({
      ...pick(dto, [
        'contractAmount', 'contractAmountLenders', 'contractAmountWithGst', 'contractDrawnAmount', 'contractToBe',
        'variationsAmount', 'variationsAmountWithGst', 'variationsDrawnAmount', 'variationsToBe',
        'totalDrawnAmount', 'totalAmountWithGst', 'totalAmount', 'totalToBe',
      ]),
      isContractAmountManual: false,
      isContractAmountWithGstManual: false,
    }),
    rules: dto.drawRules
      // include lender's schedule draw rule only if it is allowed and job does not have invoices
      .filter(dto.isLenderScheduleAllowed && isEmpty(invoices) || currentRule && isLendersScheduleCode(currentRule.code)
        ? stubTrue
        : (rule) => !isLendersScheduleCode(rule.code)),
    invoices,
    paymentSchedule: dto.paymentSchedule,
    lendersSchedule: dto.lendersSchedule.map((item) => ({
      ...item,
      orderCostPct: item.orderCost / lenderScheduleOrderCostTotal * 100,
      placement: JobDrawsLenderScheduleItemPlacement.Regular,
    })),
    selectedInvoiceId: get(first(invoices), 'invoiceId'),
    isLendersScheduleRefreshed: false,
    isDrawRuleModified: false,
    isNotProceeding: dto.isNotProceeding,
    isFinalized: dto.isFinalized,
    version: dto.version,
  };
};

export const getJobDrawsInvoiceId = (invoice: JobDrawsDetailsInvoice) => invoice.invoiceId;

function createTotalInvoice(invoices: JobDrawsDetailsInvoice[],
                            placement: JobDrawsInvoicePlacement,
                            predicate: PredicateFn<JobDrawsDetailsInvoice>): JobDrawsDetailsInvoice {
  let amount = 0;
  let balance = 0;

  invoices
    .filter(predicate)
    .forEach((invoice) => {
      amount += invoice.amountWithGst || 0;
      balance += invoice.balance || 0;
    });

  return <JobDrawsDetailsInvoice>{
    invoiceId: `total:${placement}`,
    amountWithGst: amount,
    balance,
    placement,
  };
}

export const calculateInvoiceTotals = (invoices: JobDrawsDetailsInvoice[]) => [
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalContract,
    (invoice) => invoice.type === InvoiceType.Draw),
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalChangeOrders,
    (invoice) => invoice.type === InvoiceType.ChangeOrder),
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalDraws,
    (invoice) => invoice.type === InvoiceType.Draw || invoice.type === InvoiceType.ChangeOrder),
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalCrNotes,
    (invoice) => invoice.type === InvoiceType.CreditNote),
];

export const createTotalLendersScheduleItem = (items: JobDrawsLendersScheduleItem[],
                                               placement: JobDrawsLenderScheduleItemPlacement,
                                               orderCost: number,
                                               orderCostPct?: number,
                                               amountDrawn?: number,
                                               balance?: number): JobDrawsLendersScheduleItem =>
  <JobDrawsLendersScheduleItem><any>({
    orderCost,
    orderCostPct,
    placement,
    amountDrawn,
    balance,
  });

export const calculateLendersScheduleTotals = (items: JobDrawsLendersScheduleItem[],
                                               totalOrderCost: number) => {
  const orderCost = sumBy(items, 'orderCost');
  const amountDrawn = sumBy(items, 'amountDrawn');
  const balance = sumBy(items, 'balance');
  return [
    createTotalLendersScheduleItem(
      items,
      JobDrawsLenderScheduleItemPlacement.Total,
      orderCost,
      orderCost ? orderCost / totalOrderCost * 100 : 0,
      amountDrawn,
      balance),
  ];
};

const JOB_DRAWS_DETAILS_INFO_ENTITY_CONFIG: EntityConfig<JobDrawsDetailsInfo> = omitBy(
  <any>{
    contractAmount: { required: true, type: NumberTypes.Price },
    contractAmountWithGst: IS_REGION_AU_OR_NZ ? { required: true, type: NumberTypes.Price } : void 0,
  },
  isNil);

export const validateJobDrawsDetailsInfo = entityValidatorByEntityConfig(JOB_DRAWS_DETAILS_INFO_ENTITY_CONFIG);

export const jobSharedDocSetToDto = (model: JobSharedDocSet): JobSharedDocNewSetDto[] => compact([
  isEmpty(model.contract)
    ? void 0
    : {
      type: JobSharedDocType.Contract,
      files: map(model.contract, communicationAttachmentFromFile),
    },
  isEmpty(model.colorSelection)
    ? void 0
    : {
      type: JobSharedDocType.ColorSelection,
      files: map(model.colorSelection, communicationAttachmentFromFile),
    },
  isEmpty(model.specification)
    ? void 0
    : {
      type: JobSharedDocType.Specification,
      files: map(model.specification, communicationAttachmentFromFile),
    },
  isEmpty(model.coQuotations)
    ? void 0
    : {
      type: JobSharedDocType.ChangeOrderQuotations,
      files: map(model.coQuotations, communicationAttachmentFromFile),
    },
  isEmpty(model.plans)
    ? void 0
    : {
      type: JobSharedDocType.Plans,
      files: map(model.plans, communicationAttachmentFromFile),
    },
  isEmpty(model.jobLetters)
    ? void 0
    : {
      type: JobSharedDocType.JobLetters,
      files: map(model.jobLetters, communicationAttachmentFromFile),
    },
]);
